import { useCallback, useEffect } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import {
  getCatalogKnmYearsSelectOptions,
  getUserKnoSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { catalogKnmYearsGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import styles from './AnalyticsFiltersModal.module.scss';
import { IAnalyticsFilter } from '../interfaces/IAnalyticsFilter';

interface IAnalyticsFiltersModalProps {
  visible: boolean;
  onClose: () => void;
  filter: IAnalyticsFilter;
  yearsOptions: IYearSelectOption[];
  knoSelectOptions: IUserOrganisationSelectOption[];
  setFilter: (filter: IAnalyticsFilter) => void;
  fetchYears: () => void;
  onApply: () => void;
}

const AnalyticsFiltersModalWithoutStore = ({
  filter,
  onClose,
  setFilter,
  visible,
  fetchYears,
  onApply,
  knoSelectOptions,
  yearsOptions,
}: IAnalyticsFiltersModalProps) => {
  const handleFiltersFormSubmit = useCallback(
    (values: IAnalyticsFilter) => {
      setFilter(values);
      onApply();
      onClose();
    },
    [onApply, onClose, setFilter],
  );

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  const renderFiltersForm = useCallback(
    ({ handleSubmit }: FormRenderProps<IAnalyticsFilter>) => (
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Фильтры" />
        <ModalBody border="bottom">
          <Row>
            <FieldBlock label="Наименование органа контроля (надзора)" labelFontSize="small">
              <SelectInputField id="kno" name="kno" options={knoSelectOptions} isMulti isSearchable />
            </FieldBlock>
          </Row>
          <Row>
            <CheckboxField
              className={styles.FilterFormGroupCheckbox}
              id="includeChildProsec"
              name="includeChildProsec"
              label="Включая подчиненные прокуратуры"
              theme="system"
              type="checkbox"
            />
          </Row>
          <Row notWrap>
            <FieldBlock size="half" label="Период проведения" labelFontSize="small">
              <div className="shared-row_align-start">
                <DatePickerField name="fromDate" />
                <div className="shared-divider">—</div>
                <DatePickerField name="toDate" />
              </div>
            </FieldBlock>
            <FieldBlock size="half" label="Год проведения КНМ" labelFontSize="small">
              <SelectInputField name="year" options={yearsOptions} />
            </FieldBlock>
          </Row>
        </ModalBody>
        <ModalActions placement="left">
          <Button type="submit" color="primary" size="large" text="Применить" />
        </ModalActions>
      </form>
    ),
    [knoSelectOptions, yearsOptions],
  );

  const validateForm = useCallback((values: IAnalyticsFilter) => {
    const validateDatePicker = (fromDate: string | undefined, toDate: string | undefined, startDate?: boolean) => {
      if (
        (toDate || fromDate) &&
        (!formatDate({ date: toDate || '', dateFormat: DATE_FORMAT_LONG_FNS }) ||
          !formatDate({ date: fromDate || '', dateFormat: DATE_FORMAT_LONG_FNS }))
      ) {
        return 'Некорректный период';
      }
      if (startDate && toDate && fromDate) {
        return makeNoLaterThenValidator(
          'Дата начала периода должна быть меньше или равна дате окончания',

          () => toDate,
        )(fromDate);
      }

      if (fromDate && toDate) {
        return makeNoSoonerThenValidator(
          'Дата окончания периода должна быть больше или равна дате начала',

          () => fromDate,
        )(toDate);
      }

      if ((toDate && !fromDate && startDate) || (fromDate && !toDate && !startDate)) return 'Поле не может быть пустым';
    };

    const validateYear = (fromYear: number | undefined, toYear: number | undefined, year?: string | undefined) => {
      if (year && ((fromYear && fromYear > +year) || (toYear && toYear < +year))) {
        return 'Год проведения не относится к выбранному периоду';
      }
    };

    return {
      fromDate: validateDatePicker(values?.fromDate, values?.toDate, true),
      toDate: validateDatePicker(values?.fromDate, values?.toDate),
      year: validateYear(
        parseDate({ date: values.fromDate || '', parseFormat: DATE_FORMAT_LONG_FNS })?.getFullYear(),
        parseDate({ date: values.toDate || '', parseFormat: DATE_FORMAT_LONG_FNS })?.getFullYear(),
        values.year?.value,
      ),
    };
  }, []);

  return (
    <Modal visible={visible} onClose={onClose} modalBodyClassName={styles.Modal}>
      <Form<IAnalyticsFilter>
        initialValues={filter}
        render={renderFiltersForm}
        onSubmit={handleFiltersFormSubmit}
        validate={validateForm}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  yearsOptions: getCatalogKnmYearsSelectOptions(state),
  knoSelectOptions: getUserKnoSelectOptions(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootActions>) => ({
  fetchYears: () => dispatch(catalogKnmYearsGet()),
});

export const AnalyticsFiltersModal = connect(mapStateToProps, mapDispatchToProps)(AnalyticsFiltersModalWithoutStore);
